/* eslint-disable complexity */
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { OpenApiRouteResultType } from '../../../common/src/routes/openApiTypes';
import { useTranslate } from '../mixins/translate/translate';
import { openApiFetch } from '../store/openApiFetch';
import { Roles } from '../utils/enums/Roles';
import { GlobalSettings, isAdministratorPortal } from '../utils/getGlobalSettings';
import { isCustomerVersion } from '../utils/isCustomerVersion';
import { replaceItems } from '../utils/replaceItems';
import { useUserHasRole } from './utils/authorization/useUserHasRole';
import RoleDependent from './utils/RoleDependent';

export function MenuItem(props: {
    children?: undefined | React.ReactNode;
    text: string;
    icon: string;
    link: string;
    isActive?: undefined | boolean;
    target?: undefined | string;
}) {
    if (props.target === '_blank') {
        return (
            <li className={props.isActive ? 'active' : ''}>
                <a href={props.link} target={props.target}>
                    <span className={`fa ${props.icon}`}></span>
                    <span className="sidebar-title">{props.text}</span>
                </a>
            </li>
        );
    }

    return (
        <li className={props.isActive ? 'active' : ''}>
            <Link to={props.link} target={props.target}>
                <span className={`fa ${props.icon}`}></span>
                <span className="sidebar-title">{props.text}</span>
            </Link>
        </li>
    );
}

export function MenuGroup(props: {
    children?: undefined | React.ReactNode;
    text: string;
    icon: string;
    onClick: () => void;
    isOpen?: undefined | boolean;
}) {
    return (
        <li>
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    props.onClick();
                }}
                className={classNames({ 'accordion-toggle': true, 'menu-open': props.isOpen })}
            >
                <span className={`fa ${props.icon}`}></span>
                <span className="sidebar-title">{props.text}</span>
                <span className="caret"></span>
            </a>
            <ul className="nav sub-nav">{props.children}</ul>
        </li>
    );
}

type SurveyPermissionResult = OpenApiRouteResultType<'/api/surveys/permissions', 'get'>;
type ObjectPermissionResult = OpenApiRouteResultType<'/api/objects/permissions', 'get'>;
type LeftMenuState = {
    objectsMenuOpen: boolean;
    surveysMenuOpen: boolean;
    settingsMenuOpen: boolean;
    maintenanceMeasuresMenuOpen: boolean;
    batchesMenuOpen: boolean;
    jobsMenuOpen: boolean;
    aiMenuOpen: boolean;
    zxyExchangeMenuOpen: boolean;
    permissions?: { surveys: SurveyPermissionResult; objects: ObjectPermissionResult };
};

export function LeftMenu(props: {
    locationPathname: string;
    isSurveyorOrOperatorClient: boolean;
    globalSettings: GlobalSettings;
    useBatches: boolean;
    emailAddress: string;
    useEquipmentManagement: boolean;
    useProductTitle?: boolean | undefined;
}) {
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const translateKey = useTranslate();

    function isMenuItemActive(name: string) {
        const lowerCasePathname = props.locationPathname.toLowerCase();
        if (lowerCasePathname.indexOf(name) !== -1) {
            return true;
        }
        if (name === 'objectsList' && lowerCasePathname === '/objects') {
            return true;
        }
        if (name === 'objectsMap' && lowerCasePathname === '/objects/map') {
            return true;
        }
        if (name === 'surveysList' && lowerCasePathname === '/surveys') {
            return true;
        }
        if (name === 'surveysMap' && lowerCasePathname === '/surveys/map') {
            return true;
        }
        if (name === 'longRunning' && lowerCasePathname === '/surveys/longrunning') {
            return true;
        }
        if (name === 'notCompleted' && lowerCasePathname === '/surveys/notcompleted') {
            return true;
        }
        if (name === 'surveysInvestigation' && lowerCasePathname === '/surveys/furtherinvestigation') {
            return true;
        }
        if (name === 'maintenanceMeasuresList' && lowerCasePathname === '/maintenancemeasures/list') {
            return true;
        }
        if (name === 'maintenanceMeasuresMap' && lowerCasePathname === '/maintenancemeasures/map') {
            return true;
        }
        if (name === 'batchesList' && lowerCasePathname === '/batches') {
            return true;
        }
        if (name === 'batchesMap' && lowerCasePathname === '/batches/map') {
            return true;
        }
        if (name === 'equipment' && lowerCasePathname === '/equipment') {
            return true;
        }
        if (name === 'exchangeZXY' && lowerCasePathname === '/zxyexchange') {
            return true;
        }
        if (name === 'exchangeObjects' && lowerCasePathname === '/import/objects') {
            return true;
        }
        if (name === 'exchangeSurveys' && lowerCasePathname === '/import/surveys') {
            return true;
        }
        if (name === 'objectLabels' && lowerCasePathname === '/settings/objectlabels') {
            return true;
        }
        if (name === 'surveyLabels' && lowerCasePathname === '/settings/surveylabels') {
            return true;
        }
        if (name === 'aiSettings' && lowerCasePathname === '/ai/settings') {
            return true;
        }
        if (name === 'aiEmbedQueue' && lowerCasePathname === '/ai/embed-queue') {
            return true;
        }
        if (name === 'aiBackfill' && lowerCasePathname === '/ai/backfill') {
            return true;
        }

        return false;
    }

    function isMenuOpen(name: string) {
        const lowerCasePathname = props.locationPathname.toLowerCase();

        if (name === 'objects' && lowerCasePathname.indexOf('/objects') !== -1) {
            return true;
        }
        if (name === 'surveys' && lowerCasePathname.indexOf('/surveys') !== -1) {
            return true;
        }
        if (name === 'maintenanceMeasures' && lowerCasePathname.indexOf('/maintenancemeasures') !== -1) {
            return true;
        }
        if (name === 'batches' && lowerCasePathname.indexOf('/batches') !== -1) {
            return true;
        }
        if (name === 'ai' && lowerCasePathname.indexOf('/ai/') !== -1) {
            return true;
        }
        return false;
    }

    const [state, setState] = useState<LeftMenuState>({
        objectsMenuOpen: isMenuOpen('objects') || isMenuItemActive('objectsList') || isMenuItemActive('objectsMap') || false,
        surveysMenuOpen: isMenuOpen('surveys') || isMenuItemActive('surveysList') || isMenuItemActive('surveysMap') || false,
        settingsMenuOpen:
            isMenuOpen('objectTypesList') ||
            isMenuItemActive('elementCodesList') ||
            isMenuItemActive('unitCodesList') ||
            isMenuItemActive('objectLabels') ||
            isMenuItemActive('surveyLabels') ||
            false,
        maintenanceMeasuresMenuOpen:
            isMenuOpen('maintenanceMeasures') ||
            isMenuItemActive('maintenanceMeasuresReport') ||
            isMenuItemActive('maintenanceMeasuresList') ||
            false,
        batchesMenuOpen: isMenuOpen('batches') || isMenuItemActive('batchesList') || isMenuItemActive('batchesMap') || false,
        jobsMenuOpen: isMenuOpen('jobs') || isMenuItemActive('jobsFinished') || false,
        aiMenuOpen: isMenuOpen('ai') || false,
        zxyExchangeMenuOpen: isMenuOpen('jobs') || isMenuItemActive('jobsFinished') || false,
    });

    useEffect(() => {
        async function load() {
            const surveysPermissionsResponse = await openApiFetch('/api/surveys/permissions', 'get', {}, undefined);
            const objectsPermissionsResponse = await openApiFetch('/api/objects/permissions', 'get', {}, undefined);

            setState((i) => ({ ...i, permissions: { surveys: surveysPermissionsResponse, objects: objectsPermissionsResponse } }));
        }
        void load();
    }, []);

    function onMenuClicked(name: string) {
        const keyName = `${name}MenuOpen` as keyof LeftMenuState;
        const isOpenOld = state[keyName];
        const isOpenNew = !isOpenOld;
        setState((i) => ({ ...i, [keyName]: isOpenNew }));
    }

    return (
        <aside id="sidebar_left" className="nano nano-light affix">
            <div className="sidebar-left-content nano-content">
                <ul className="nav sidebar-menu">
                    <li className="sidebar-label pt20">Menu</li>

                    <MenuItem link="/dashboard" text={translateKey('common.dashboard')} icon="fa-home" isActive={isMenuItemActive('dashboard')} />
                    {/* Objects */}
                    {!isAdministratorPortal(props.globalSettings) && (
                        <MenuGroup
                            onClick={() => onMenuClicked('objects')}
                            text={translateKey('common.objects')}
                            isOpen={state.objectsMenuOpen}
                            icon="fa-building"
                        >
                            <MenuItem link="/objects" text={translateKey('common.list')} icon="fa-table" isActive={isMenuItemActive('objectsList')} />
                            <MenuItem
                                link="/objects/map"
                                text={translateKey('common.map')}
                                icon="fa-globe"
                                isActive={isMenuItemActive('objectsMap')}
                            />
                        </MenuGroup>
                    )}
                    {/* Surveys */}
                    {!isAdministratorPortal(props.globalSettings) && (
                        <MenuGroup
                            onClick={() => onMenuClicked('surveys')}
                            text={props.useProductTitle ? translateKey('common.productReports') : translateKey('common.surveyReports')}
                            isOpen={state.surveysMenuOpen}
                            icon="fa-camera"
                        >
                            <MenuItem link="/surveys" text={translateKey('common.list')} icon="fa-table" isActive={isMenuItemActive('surveysList')} />
                            <MenuItem
                                link="/surveys/map"
                                text={translateKey('common.map')}
                                icon="fa-globe"
                                isActive={isMenuItemActive('surveysMap')}
                            />
                            {isCustomerVersion(['zxy', 'rijksvastgoed', 'rotterdam']) && (
                                <RoleDependent
                                    allowed={[Roles.Administrator, Roles.Owner, Roles.Client]}
                                    content={
                                        <>
                                            <MenuItem
                                                link="/surveys/furtherinvestigation"
                                                text={translateKey('condition.further')}
                                                icon="fa-table"
                                                isActive={isMenuItemActive('surveysInvestigation')}
                                            />
                                        </>
                                    }
                                />
                            )}
                            {state.permissions?.surveys.canReadNotCompletedSurveys === true && (
                                <MenuItem
                                    link="/surveys/notCompleted"
                                    text={translateKey('common.progress')}
                                    icon="fa-clock-o"
                                    isActive={isMenuItemActive('notCompleted')}
                                />
                            )}
                            {state.permissions?.surveys.canReadLongRunningSurveys === true && (
                                <MenuItem
                                    link="/surveys/longRunning"
                                    text={
                                        props.useProductTitle
                                            ? translateKey('product.longRunningProducts')
                                            : translateKey('survey.longRunningSurveys')
                                    }
                                    icon="fa-clock-o"
                                    isActive={isMenuItemActive('longRunning')}
                                />
                            )}
                        </MenuGroup>
                    )}
                    {/* Batches */}
                    {!isAdministratorPortal(props.globalSettings) && props.useBatches && (
                        <MenuGroup onClick={() => onMenuClicked('batches')} text="Batches" isOpen={state.batchesMenuOpen} icon="fa-briefcase">
                            <MenuItem link="/batches" text={translateKey('common.list')} icon="fa-table" isActive={isMenuItemActive('batchesList')} />
                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={
                                    <MenuItem
                                        link="/batches/map"
                                        text={translateKey('common.map')}
                                        icon="fa-globe"
                                        isActive={isMenuItemActive('batchesMap')}
                                    />
                                }
                            />
                        </MenuGroup>
                    )}

                    {/* Maintenance Measures */}
                    {!isAdministratorPortal(props.globalSettings) && isCustomerVersion(['zxy', 'rijksvastgoed', 'rotterdam']) && (
                        <RoleDependent
                            allowed={[Roles.Administrator, Roles.Client, Roles.Owner]}
                            content={
                                <MenuGroup
                                    onClick={() => onMenuClicked('maintenanceMeasures')}
                                    text={translateKey('maintenanceMeasure.maintenanceMeasures')}
                                    isOpen={state.maintenanceMeasuresMenuOpen}
                                    icon="fa-wrench"
                                >
                                    <MenuItem
                                        link="/maintenancemeasures/list"
                                        text={translateKey('common.list')}
                                        icon="fa-table"
                                        isActive={isMenuItemActive('maintenanceMeasuresList')}
                                    />
                                    <MenuItem
                                        link="/maintenancemeasures/map"
                                        text={translateKey('common.map')}
                                        icon="fa-globe"
                                        isActive={isMenuItemActive('maintenanceMeasuresMap')}
                                    />
                                </MenuGroup>
                            }
                        />
                    )}
                    {/* ZXY Data Exchange */}
                    {!isAdministratorPortal(props.globalSettings) && !isCustomerVersion('ronik') && props.isSurveyorOrOperatorClient && (
                        <MenuGroup
                            onClick={() => onMenuClicked('zxyExchange')}
                            text="Data Exchange"
                            isOpen={state.zxyExchangeMenuOpen}
                            icon="fa-exchange"
                        >
                            <MenuItem link="/zxyExchange" text="ZXY Exchange" icon="fa-exchange" isActive={isMenuItemActive('exchangeZXY')} />
                            <MenuItem
                                link="/import/objects"
                                text={translateKey('common.importItem', translateKey('common.objects'))}
                                icon="fa-upload"
                                isActive={isMenuItemActive('exchangeObjects')}
                            />
                            <MenuItem
                                link="/import/surveys"
                                text={translateKey('common.importItem', translateKey('common.surveys'))}
                                icon="fa-upload"
                                isActive={isMenuItemActive('exchangeSurveys')}
                            />
                        </MenuGroup>
                    )}

                    {/* UT projects */}
                    <>
                        {props.useEquipmentManagement && (
                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={
                                    <MenuItem
                                        link="/equipment"
                                        text={translateKey('equipment.management')}
                                        icon="fa-briefcase"
                                        isActive={isMenuItemActive('equipment')}
                                    />
                                }
                            />
                        )}
                        {!isAdministratorPortal(props.globalSettings) && isCustomerVersion('ronik') && (
                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={
                                    <MenuItem link="/projects" text="UT Projects" icon="fa-bar-chart-o" isActive={isMenuItemActive('projects')} />
                                }
                            />
                        )}
                    </>

                    {/* Help */}
                    {!isAdministratorPortal(props.globalSettings) && (
                        <>
                            <li className="sidebar-label pt15 ">Help</li>
                            <MenuItem link="/faq" text="FAQ" icon="fa-life-ring" isActive={isMenuItemActive('faq')} />
                            <MenuItem
                                link={replaceItems(props.globalSettings.sendSupportRequestLink, [{ search: '{email}', replace: props.emailAddress }])}
                                target="_blank"
                                text={translateKey('support.support')}
                                icon="fa-comments-o"
                                isActive={isMenuItemActive('support')}
                            />
                        </>
                    )}
                    {/* Companies */}
                    <RoleDependent
                        allowed={[Roles.AccountManager, Roles.ConfigurationManager]}
                        content={<li className="sidebar-label pt15">{translateKey('common.system')}</li>}
                    />
                    {!isAdministratorPortal(props.globalSettings) && (
                        <RoleDependent
                            allowed={[Roles.AccountManager]}
                            content={
                                <MenuItem
                                    link="/companies"
                                    text={translateKey('common.companies')}
                                    icon="fa-building"
                                    isActive={isMenuItemActive('companies')}
                                />
                            }
                        />
                    )}
                    {/* Settings */}
                    <RoleDependent
                        allowed={[Roles.ConfigurationManager]}
                        content={
                            <>
                                <MenuGroup
                                    onClick={() => onMenuClicked('settings')}
                                    text={translateKey('common.settings')}
                                    isOpen={state.settingsMenuOpen}
                                    icon="fa-cogs"
                                >
                                    {isAdmin && (
                                        <MenuItem
                                            link="/settings/inspection-forms"
                                            text={translateKey('inspectionForms.title')}
                                            icon="fa-cogs"
                                            isActive={isMenuItemActive('inspection-forms')}
                                        />
                                    )}
                                    <MenuItem
                                        link="/settings/objecttypes"
                                        text={translateKey('common.objectTypes')}
                                        icon="fa-cogs"
                                        isActive={isMenuItemActive('objecttypes')}
                                    />
                                    {state.permissions?.objects.canManageLabels === true && (
                                        <MenuItem
                                            link="/settings/objectLabels"
                                            text={translateKey('object.labels')}
                                            icon="fa-tag"
                                            isActive={isMenuItemActive('objectLabels')}
                                        />
                                    )}
                                    {state.permissions?.surveys.canManageLabels === true && (
                                        <MenuItem
                                            link="/settings/surveyLabels"
                                            text={translateKey('survey.labels')}
                                            icon="fa-tag"
                                            isActive={isMenuItemActive('surveyLabels')}
                                        />
                                    )}
                                    <MenuItem
                                        link="/settings/taskdefinitions"
                                        text={translateKey('workflow.checklist')}
                                        icon="fa-cogs"
                                        isActive={isMenuItemActive('checklist')}
                                    />
                                    {isAdmin && (
                                        <MenuItem
                                            link="/settings/elementcodes"
                                            text={translateKey('common.elements')}
                                            icon="fa-cogs"
                                            isActive={isMenuItemActive('elementcodes')}
                                        />
                                    )}
                                    <MenuItem
                                        link="/settings/unitcodes"
                                        text={translateKey('common.units')}
                                        icon="fa-cogs"
                                        isActive={isMenuItemActive('unitcodes')}
                                    />
                                    {isAdmin && props.useEquipmentManagement && (
                                        <>
                                            <MenuItem
                                                link="/settings/equipmenttypes"
                                                text={translateKey('equipment.types')}
                                                icon="fa-cogs"
                                                isActive={isMenuItemActive('equipmenttypes')}
                                            />
                                        </>
                                    )}
                                    {!isAdministratorPortal(props.globalSettings) && isAdmin && (
                                        <MenuItem
                                            link="/settings/registrations"
                                            text="ZXY Exchange"
                                            icon="fa-cogs"
                                            isActive={isMenuItemActive('registrations')}
                                        />
                                    )}
                                </MenuGroup>
                            </>
                        }
                    />
                    <RoleDependent
                        allowed={[Roles.Administrator]}
                        content={
                            <>
                                <MenuGroup onClick={() => onMenuClicked('jobs')} text="Jobs" isOpen={state.jobsMenuOpen} icon="fa-flag-checkered">
                                    <MenuItem
                                        link="/jobs"
                                        text={translateKey('worker.runningJobs')}
                                        icon="fa-flag-checkered"
                                        isActive={isMenuItemActive('jobs')}
                                    />
                                    <MenuItem
                                        link="/finished"
                                        text={translateKey('worker.finishedJobs')}
                                        icon="fa-flag-checkered"
                                        isActive={isMenuItemActive('finished')}
                                    />
                                </MenuGroup>
                                <MenuGroup onClick={() => onMenuClicked('ai')} text="AI" isOpen={state.aiMenuOpen} icon="fa-certificate">
                                    <MenuItem link="/ai/settings" text="Settings" icon="fa-cogs" isActive={isMenuItemActive('aiSettings')} />
                                    <MenuItem
                                        link="/ai/embed-queue"
                                        text="Embed Queue"
                                        icon="fa-database"
                                        isActive={isMenuItemActive('aiEmbedQueue')}
                                    />
                                    <MenuItem link="/ai/backfill" text="BackFill" icon="fa-history" isActive={isMenuItemActive('aiBackfill')} />
                                </MenuGroup>
                                <MenuItem link="/auditlog" text="Audit log" icon="fa-file-text" isActive={isMenuItemActive('auditlog')} />
                            </>
                        }
                    />
                </ul>
            </div>
        </aside>
    );
}
